/**
 * Kenra — slider progress bar
 *
 * Companion to Dawn's <slider-component> (defined in global.js). Renders a
 * Swiper-style progress fill that tracks the active PAGE of an associated
 * slider, replacing Dawn's "1 / N" numeric counter. Clones the look of the
 * kenraprofessional.com blog hero's swiper-pagination-progressbar.
 *
 * Markup contract (rendered by snippets/kenra-slider-progress.liquid):
 *
 *   <slider-component>
 *     <ul id="Slider-XYZ" class="… slider slider--everywhere">
 *       <li class="… slider__slide">…</li>
 *     </ul>
 *     <kenra-slider-progress class="kenra-slider-progress" for="Slider-XYZ">
 *       <span class="kenra-slider-progress__fill"></span>
 *     </kenra-slider-progress>
 *   </slider-component>
 *
 * `for` = the id of the slider <ul> (the [id^="Slider-"] element Dawn uses).
 * Fill scaleX = currentPage / pageCount, where a page = slidesPerView slides.
 * Sets [data-locked] on itself when there's only one page (CSS hides it).
 */
(function () {
  'use strict';

  if (window.customElements && customElements.get('kenra-slider-progress')) return;

  function slideOffset(slides) {
    if (!slides || slides.length < 2) {
      return slides && slides[0] ? slides[0].clientWidth : 0;
    }
    return slides[1].offsetLeft - slides[0].offsetLeft;
  }

  function getSlidesPerView(slider, slides) {
    var offset = slideOffset(slides);
    if (offset <= 0) return 1;
    return Math.max(1, Math.round(slider.clientWidth / offset));
  }

  function getPageCount(slides, slidesPerView) {
    if (!slides.length || slidesPerView <= 0) return 1;
    return Math.max(1, Math.ceil(slides.length / slidesPerView));
  }

  function getCurrentPage(slider, slides, slidesPerView, pageCount) {
    if (pageCount < 2) return 0;
    var offset = slideOffset(slides);
    if (offset <= 0) return 0;
    var index = Math.round(slider.scrollLeft / (offset * slidesPerView));
    return Math.max(0, Math.min(pageCount - 1, index));
  }

  class KenraSliderProgress extends HTMLElement {
    connectedCallback() {
      this.fill = this.querySelector('.kenra-slider-progress__fill');
      var targetId = this.getAttribute('for');
      this.slider = targetId ? document.getElementById(targetId) : null;
      if (!this.slider || !this.fill) return;

      this.update = this.update.bind(this);
      this.slider.addEventListener('scroll', this.update, { passive: true });
      window.addEventListener('resize', this.update);
      if ('ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(this.update);
        this.resizeObserver.observe(this.slider);
      }
      this.update();
    }

    disconnectedCallback() {
      if (this.slider) this.slider.removeEventListener('scroll', this.update);
      window.removeEventListener('resize', this.update);
      if (this.resizeObserver) this.resizeObserver.disconnect();
    }

    update() {
      var slides = this.slider.querySelectorAll('.slider__slide');
      if (!slides.length) return;
      var slidesPerView = getSlidesPerView(this.slider, slides);
      var pageCount = getPageCount(slides, slidesPerView);
      var page = getCurrentPage(this.slider, slides, slidesPerView, pageCount);
      var progress = pageCount > 0 ? (page + 1) / pageCount : 0;

      if (pageCount < 2) {
        this.setAttribute('data-locked', '');
      } else {
        this.removeAttribute('data-locked');
      }
      this.fill.style.transform = 'scaleX(' + progress + ')';
    }
  }

  customElements.define('kenra-slider-progress', KenraSliderProgress);
})();
